import { fileTypeName } from "./file-type";

export const sortNone = 0;
export const sortUp = 1;
export const sortDown = 2;

export function cmpStringsNoCase(s1: string, s2: string): number {
  return s1.localeCompare(s2, undefined, { sensitivity: "base" });
}

export function cmpStringsNaturalNoCase(s1: string, s2: string): number {
  return s1.localeCompare(s2, navigator.languages?.[0] || navigator.language, {
    numeric: true,
    ignorePunctuation: true,
    // sensitivity: "base",
  });
}

// Note: I think it's un-intuitive but in explorer and opus
// "sort down" means "large / z first" and "sort up" means "small / a first"

interface Entry {
  isDir: boolean;
  name: string;
  lastMod: number;
  size: number;
}

function nameSortUp(e1: Entry, e2: Entry): number {
  // directories sort before files
  if (e1.isDir && !e2.isDir) {
    return -1;
  }
  if (!e1.isDir && e2.isDir) {
    return 1;
  }
  if (e1.name == "..") {
    return -1;
  }
  if (e2.name == "..") {
    return 1;
  }
  return cmpStringsNaturalNoCase(e1.name, e2.name);
}

function nameSortDown(e1: Entry, e2: Entry): number {
  // directories sort before files
  if (e1.isDir && !e2.isDir) {
    return -1;
  }
  if (!e1.isDir && e2.isDir) {
    return 1;
  }
  if (e1.name == "..") {
    return -1;
  }
  if (e2.name == "..") {
    return 1;
  }
  return cmpStringsNoCase(e2.name, e1.name);
}

function cmdNum(n1: number, n2: number): number {
  if (n1 > n2) {
    return 1;
  }
  if (n1 == n2) {
    return 0;
  }
  return -1;
}

function lastModSortUp(e1: Entry, e2: Entry): number {
  // directories sort before files
  if (e1.isDir && !e2.isDir) {
    return -1;
  }
  if (!e1.isDir && e2.isDir) {
    return 1;
  }

  // directories sort by name
  if (e1.isDir && e2.isDir) {
    return nameSortUp(e1, e2);
  }

  return cmdNum(e1.lastMod, e2.lastMod);
}

function lastModSortDown(e1: Entry, e2: Entry): number {
  // directories sort before files
  if (e1.isDir && !e2.isDir) {
    return -1;
  }
  if (!e1.isDir && e2.isDir) {
    return 1;
  }

  // directories sort by name
  if (e1.isDir && e2.isDir) {
    return nameSortUp(e1, e2);
  }

  return cmdNum(e2.lastMod, e1.lastMod); // reverse
}

function sizeSortUp(e1: Entry, e2: Entry): number {
  // directories sort before files
  if (e1.isDir && !e2.isDir) {
    return -1;
  }
  if (!e1.isDir && e2.isDir) {
    return 1;
  }

  // directories sort by name
  if (e1.isDir && e2.isDir) {
    return nameSortUp(e1, e2);
  }

  return cmdNum(e1.size, e2.size);
}

function sizeSortDown(e1: Entry, e2: Entry): number {
  // directories sort before files
  if (e1.isDir && !e2.isDir) {
    return -1;
  }
  if (!e1.isDir && e2.isDir) {
    return 1;
  }

  // directories sort by name
  if (e1.isDir && e2.isDir) {
    return nameSortUp(e1, e2);
  }

  return cmdNum(e2.size, e1.size); // reverse
}

// By type, then by name: files of one type stay together in name order.
// Files without a type come last.
function typeSortUp(e1: Entry, e2: Entry): number {
  // directories sort before files
  if (e1.isDir && !e2.isDir) {
    return -1;
  }
  if (!e1.isDir && e2.isDir) {
    return 1;
  }

  // directories sort by name
  if (e1.isDir && e2.isDir) {
    return nameSortUp(e1, e2);
  }

  let t1 = fileTypeName(e1.name, false);
  let t2 = fileTypeName(e2.name, false);
  if (t1 != t2) {
    if (t1 == "") {
      return 1;
    }
    if (t2 == "") {
      return -1;
    }
    return cmpStringsNoCase(t1, t2);
  }
  return nameSortUp(e1, e2);
}

function typeSortDown(e1: Entry, e2: Entry): number {
  // directories sort before files
  if (e1.isDir && !e2.isDir) {
    return -1;
  }
  if (!e1.isDir && e2.isDir) {
    return 1;
  }

  // directories sort by name
  if (e1.isDir && e2.isDir) {
    return nameSortUp(e1, e2);
  }

  let t1 = fileTypeName(e1.name, false);
  let t2 = fileTypeName(e2.name, false);
  if (t1 != t2) {
    if (t1 == "") {
      return 1;
    }
    if (t2 == "") {
      return -1;
    }
    return cmpStringsNoCase(t2, t1); // reverse
  }
  return nameSortUp(e1, e2);
}

type SortFunction = (e1: Entry, e2: Entry) => number;

export function sortFiles(
  entries: Entry[],
  nameSort: number,
  sizeSort: number,
  lastModSort: number,
  typeSort: number = sortNone,
) {
  let fnSort: SortFunction = nameSortUp;

  if (nameSort == sortDown) {
    fnSort = nameSortDown;
  }

  if (sizeSort == sortUp) {
    fnSort = sizeSortUp;
  } else if (sizeSort == sortDown) {
    fnSort = sizeSortDown;
  }
  if (lastModSort == sortUp) {
    fnSort = lastModSortUp;
  } else if (lastModSort == sortDown) {
    fnSort = lastModSortDown;
  }

  if (typeSort == sortUp) {
    fnSort = typeSortUp;
  } else if (typeSort == sortDown) {
    fnSort = typeSortDown;
  }

  entries.sort(fnSort);
}
